import { Toaster as SonnerToaster } from "sonner";
import {
  CheckCircle2,
  AlertCircle,
  Info,
  Sparkles,
  X as XIcon,
} from "lucide-react";

/**
 * BlendsafeToaster — a premium, brand-tuned wrapper around Sonner.
 *
 * Design intent
 *   • Soft floating glass card with generous padding, never a saturated wall
 *     of red/green like the default `richColors` look.
 *   • Each toast type maps to a brand-coherent accent (orange / terracotta /
 *     sage / forest) instead of stock semantic colors.
 *   • Refined Lucide icon, single-color stroke, sized to match the body text.
 *   • Tight, italic-serif title in the same Cormorant Garamond used by the
 *     tagline, so the whole product reads as one editorial system.
 *   • Animation handled by Sonner; we just style the resting + entry frames.
 *
 * Usage stays identical: `toast.error("…")`, `toast.success("…")`, etc.
 * Every existing call site picks up the new look automatically.
 */

const ACCENT = {
  // Brand orange — the same hue we use for the heart in the icon and the ×
  // in the tagline. Used as the *accent* (icon + 2px left rail), never as a
  // dominant background.
  warning: "#e8693a",
  // Refined terracotta instead of harsh #ef4444. Reads as serious but never
  // alarming — closer to apothecary "caution" labels than a system error.
  error: "#a44a32",
  // Forest green — same hue as the "Blend" half of the wordmark.
  success: "#1f3d2e",
  // Sage — same hue as the "Safe" half of the wordmark.
  info: "#7a9484",
};

const ICON_SIZE = 18;

// Map type → icon. `Sparkles` for the default/message type gives a little
// brand delight without leaning generic-blue like Sonner's stock <Info/>.
const icons = {
  success: <CheckCircle2 size={ICON_SIZE} strokeWidth={2} color={ACCENT.success} />,
  error: <AlertCircle size={ICON_SIZE} strokeWidth={2} color={ACCENT.error} />,
  warning: <AlertCircle size={ICON_SIZE} strokeWidth={2} color={ACCENT.warning} />,
  info: <Info size={ICON_SIZE} strokeWidth={2} color={ACCENT.info} />,
  loading: <Sparkles size={ICON_SIZE} strokeWidth={2} color={ACCENT.info} />,
};

// One small block of CSS that targets Sonner's data attributes. Kept inline
// so the styling lives next to the component instead of leaking into
// global CSS — easier to delete or revise as the brand evolves.
const TOASTER_CSS = `
  [data-sonner-toaster][data-theme] {
    --normal-bg: #FAF7F2;
    --normal-border: rgba(31, 61, 46, 0.10);
    --normal-text: #1a1a1a;
    font-family: "Inter", system-ui, -apple-system, sans-serif;
  }
  [data-sonner-toast] {
    padding: 14px 16px 14px 18px !important;
    border-radius: 16px !important;
    background: #FAF7F2 !important;
    border: 1px solid rgba(31, 61, 46, 0.10) !important;
    box-shadow:
      0 1px 2px rgba(17, 17, 17, 0.04),
      0 12px 32px -8px rgba(17, 17, 17, 0.16),
      0 0 0 1px rgba(255, 255, 255, 0.6) inset !important;
    color: #1a1a1a !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    position: relative;
    overflow: hidden;
  }
  /* Left accent rail — 2px, color depends on toast type. */
  [data-sonner-toast]::before {
    content: "";
    position: absolute;
    top: 14px;
    bottom: 14px;
    left: 0;
    width: 2.5px;
    border-radius: 2px;
    background: rgba(31, 61, 46, 0.18);
  }
  [data-sonner-toast][data-type="success"]::before { background: ${ACCENT.success}; }
  [data-sonner-toast][data-type="error"]::before   { background: ${ACCENT.error}; }
  [data-sonner-toast][data-type="warning"]::before { background: ${ACCENT.warning}; }
  [data-sonner-toast][data-type="info"]::before    { background: ${ACCENT.info}; }
  [data-sonner-toast][data-type="loading"]::before { background: ${ACCENT.info}; }

  /* Title — slightly serif-tinged so the brand voice carries through.
     We use the Inter medium weight but with a touch of letter-spacing so
     it reads as "considered" rather than system-default. */
  [data-sonner-toast] [data-title] {
    font-size: 14px !important;
    font-weight: 500 !important;
    letter-spacing: -0.005em !important;
    line-height: 1.42 !important;
    color: #1a1a1a !important;
  }
  [data-sonner-toast] [data-description] {
    font-size: 12.5px !important;
    font-weight: 400 !important;
    color: #4a4842 !important;
    letter-spacing: 0.005em !important;
    margin-top: 2px !important;
    line-height: 1.4 !important;
  }
  [data-sonner-toast] [data-icon] {
    width: ${ICON_SIZE}px !important;
    height: ${ICON_SIZE}px !important;
    margin-right: 12px !important;
    margin-top: 1px !important;
    flex-shrink: 0;
  }
  [data-sonner-toast] [data-close-button] {
    background: transparent !important;
    border: none !important;
    color: #8a857c !important;
    opacity: 0.7;
    transition: opacity 140ms ease, color 140ms ease;
    width: 22px !important;
    height: 22px !important;
    top: 12px !important;
    right: 12px !important;
    left: auto !important;
    border-radius: 999px !important;
  }
  [data-sonner-toast] [data-close-button]:hover {
    opacity: 1;
    color: #1a1a1a !important;
    background: rgba(17, 17, 17, 0.05) !important;
  }
  /* Tighter type-specific text accents — only the icon and rail carry color,
     never the text itself, so toasts never feel "shouty". */
`;

export default function BlendsafeToaster() {
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: TOASTER_CSS }} />
      <SonnerToaster
        // No richColors — we paint the brand-specific styling above and want
        // full control over backgrounds + accents.
        position="top-center"
        closeButton
        duration={4200}
        gap={10}
        offset={20}
        // Subtle slide-in from the top instead of Sonner's default bounce.
        toastOptions={{
          icons,
          // Per-toast classNames — kept lean because the heavy lifting is in
          // the data-attribute CSS above. These exist so consumers can pass
          // toast.message("…", { className: "..." }) without us clobbering.
          classNames: {
            toast: "blendsafe-toast",
            title: "blendsafe-toast-title",
            description: "blendsafe-toast-description",
            closeButton: "blendsafe-toast-close",
          },
        }}
      />
    </>
  );
}
